// export default function getKeysFromPreviousQueries(dataSet: any, queryKey: any) {
//   // --- 1. Deconstruct the query key's filters ---
//   // Find the object part of the key, which contains the filters.
//   const queryObj = queryKey.find((item: any) => typeof item === 'object' && item !== null);
//   // Extract the filters from the query object. If none, use an empty array.
//   const queryFilters = queryObj?.filters || [];

//   // If there are no filters to search by, return an empty array.
//   if (queryFilters.length === 0) {
//     return [];
//   }

//   /**
//    * --- 2. Helper function to compare two filter objects ---
//    * It checks for equality in field, operator, and value.
//    * The 'value' is converted to a string to handle type differences (e.g., 15055 vs "15055").
//    * @param {object} f1 - First filter object.
//    * @param {object} f2 - Second filter object.
//    * @returns {boolean} - True if filters are logically equivalent.
//    */
//   const areFiltersEqual = (f1: any, f2: any) => {
//     if (!f1 || !f2) return false;
//     return (
//       f1.field === f2.field && f1.operator === f2.operator && String(f1.value) === String(f2.value)
//     );
//   };

//   // --- 3. Filter the dataSet to find all matching entries ---
//   const foundEntries = dataSet.filter(([key, _value]: any) => {
//     // Ensure the key is a valid array before processing.
//     if (!key || !Array.isArray(key)) return false;

//     // Deconstruct the current key from the dataset being checked.
//     const keyObj = key.find((item) => typeof item === 'object' && item !== null);

//     // The dataset key must have an object with a 'filters' array to be a candidate.
//     if (!keyObj || !Array.isArray(keyObj.filters)) {
//       return false;
//     }

//     // Check if every filter in the queryKey is present in the dataset key's filters.
//     // The order does not matter, and the dataset key can have extra filters.
//     return queryFilters.every((qFilter: any) =>
//       keyObj.filters.some((kFilter: any) => areFiltersEqual(qFilter, kFilter))
//     );
//   });

//   // --- 4. Return the key part of each found entry ---
//   return foundEntries.map(([key, _value]: any) => key);
// }
const isObject = (obj: any) => obj !== null && typeof obj === 'object' && !Array.isArray(obj);

/**
 * A deep equality comparison function for objects and arrays.
 * This removes the need for external libraries like lodash.
 * @param {*} a The first value to compare.
 * @param {*} b The second value to compare.
 * @returns {boolean} True if the values are deeply equal.
 */
function isEqual(a: any, b: any) {
  if (a === b) return true;

  if (a && b && typeof a === 'object' && typeof b === 'object') {
    if (a.constructor !== b.constructor) return false;

    let length, i;
    if (Array.isArray(a)) {
      length = a.length;
      if (length !== b.length) return false;
      for (i = length; i-- > 0; ) {
        if (!isEqual(a[i], b[i])) return false;
      }
      return true;
    }

    const keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- > 0; ) {
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    }

    for (i = length; i-- > 0; ) {
      const key = keys[i];
      if (!isEqual(a[key], b[key])) return false;
    }

    return true;
  }

  return a !== a && b !== b;
}

export default function getKeysFromPreviousQueries(dataSet: any, keyToMatch: any, opts: any = {}) {
  if (!Array.isArray(dataSet) || !Array.isArray(keyToMatch)) {
    console.error('Invalid input: dataSet and keyToMatch must be arrays.');
    return [];
  }

  // Extract the filter object and other parts from the key we want to match.
  const keyToMatchFilterObject = keyToMatch.find((item) => isObject(item) && item.filters);
  const keyToMatchOtherParts = keyToMatch.filter((item) => !(isObject(item) && item.filters));

  // If the key to match doesn't have a filter object, we can't perform the logic.
  if (!keyToMatchFilterObject) {
    return [];
  }

  const filtersToMatch = keyToMatchFilterObject.filters || [];

  // Filter the main dataset
  const fullDataSet = dataSet.filter(([cachedQueryKey]) => {
    // Extract the filter object and other parts from the cached key.
    const cachedKeyFilterObject = cachedQueryKey.find(
      (item: any) => isObject(item) && item.filters
    );
    const cachedKeyOtherParts = cachedQueryKey.filter(
      (item: any) => !(isObject(item) && item.filters)
    );

    // If a cached key doesn't have a filter object, it can't be a match.
    if (!cachedKeyFilterObject) {
      return false;
    }

    // 1. Check if the non-filter parts of the keys are identical.
    // This ensures we are comparing keys from the same resource/list.
    const areOtherPartsEqual = isEqual(keyToMatchOtherParts, cachedKeyOtherParts);
    if (!areOtherPartsEqual) {
      return false;
    }

    const cachedFilters = cachedKeyFilterObject.filters || [];

    // 2. Check if every filter in `filtersToMatch` is present in `cachedFilters`.
    // This is the "subset" check.
    return filtersToMatch.every((filterToMatch: any) =>
      cachedFilters.some((cachedFilter: any) => isEqual(filterToMatch, cachedFilter))
    );
  });
  if (opts?.withData) {
    return fullDataSet;
  }
  return fullDataSet.map(([cachedQueryKey, data]) => cachedQueryKey);
}
